import React, { useRef, useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Grid,
  Slider,
  Box,
  Fab,
  CardContent,
  Typography,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import { PlayArrow, Pause } from "@mui/icons-material";
import FastForwardIcon from "@mui/icons-material/FastForward";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import DateTimeNew from "src/components/dateTime/DateTimeNew";
import PageContainer from "../../../../components/container/PageContainer";
import CustomMarker from "src/components/map/marker/CustomMarker";
import CIWFRRNew from "src/components/map/marker/CIWFRRNew";
import TempDrawer from "src/components/drawer";
import SearchIcon from "@mui/icons-material/Search";

import {
  GoogleMap,
  useLoadScript,
  Polyline,
  useGoogleMap,
} from "@react-google-maps/api";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { actionVehicleData } from "src/store/customizer/CustomizerSlice";
import moment from "moment";
import VehicleDetailsService from "src/service/vehicleDetails";
import { IconArrowBack } from "@tabler/icons";
import BlankCard from "src/components/shared/BlankCard";
import online from "../../../../assets/images/icons/online.svg";

const AllVehicleMap = () => {
  const myElementRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [coordinates, setCoordinates] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [currentHeading, setCurrentHeading] = useState(0);
  const [sliderValue, setSliderValue] = useState(0);
  const [activeInfoWindow, setActiveInfoWindow] = useState(true);
  const [from, setFrom] = useState(
    new Date().setHours(new Date().getHours() - 1)
  );
  const [to, setTo] = useState(new Date());
  const [play, setPlay] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false); // Loading state
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || "",
  });

  const mapOptions = useMemo(() => ({}), []);
  const defaultCenter = useMemo(() => ({ lat: 25.612677, lng: 85.158875 }), []);
  const mapContainerStyle = useMemo(
    () => ({
      width: "100%",
      height: "90vh",
      borderRadius: "10px",
    }),
    []
  );

  const param = useParams();
  const dispatch = useDispatch();
  const { VehicleData } = useSelector((state) => state.customizer);

  const fetchDataInRange = async (start, end, vehicleNumber) => {
    try {
      setLoading(true); // Start loading
      const hoursDifference = moment(end).diff(moment(start), "hours");
      if (hoursDifference > 24) {
        let currentDate = moment(start);
        let tempCoordinates = [];
        while (currentDate <= moment(end)) {
          const fromDate = currentDate.clone().startOf("day");
          const toDate = currentDate.clone().endOf("day");
          const data = await fetchCoordinates(fromDate, toDate, vehicleNumber);
          tempCoordinates = [...tempCoordinates, ...data];
          currentDate = currentDate.add(1, "day");
        }
        setCoordinates(tempCoordinates);
        dispatch(actionVehicleData(tempCoordinates));
      } else {
        const data = await fetchCoordinates(start, end, vehicleNumber);
        // console.log("data", data);
        setCoordinates(data);
        dispatch(actionVehicleData(data));
      }
    } catch (error) {
      console.log(error);
      setCoordinates([]);
      dispatch(actionVehicleData([]));
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const fetchCoordinates = async (fromDate, toDate, vehicleNumber) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}gpsData`,
        {
          vehicle_number: vehicleNumber,
          from_date_time: moment(fromDate).format("YYYY-MM-DD HH:mm:ss"),
          to_date_time: moment(toDate).format("YYYY-MM-DD HH:mm:ss"),
        }
      );
      // const filteredData = response.data.data.filter((item) => {
      //   const gpsRawData = item.gps_rawdata.split(",");
      //   return gpsRawData[8] === "1";
      // });
      return response.data.data;
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const allVehicles = async () => {
    try {
      const res = await VehicleDetailsService.getVehicleDetails({
        vehicle_no: param.vehicle_no,
      });
      setVehicleInfo(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetch_allVehicles = async () => {
    try {
      const res = await VehicleDetailsService.getVehicleDetails({});
      setVehicles(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    allVehicles();
    fetch_allVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param.vehicle_no]);

  useEffect(() => {
    fetchDataInRange(from, to, param.vehicle_no);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param.vehicle_no]);

  function handleSliderChange(event, newValue) {
    setSliderValue(newValue);
    setCurrentLocation(VehicleData[newValue - 1]);
  }

  useEffect(() => {
    if (play) {
      const interval = setInterval(() => {
        if (sliderValue < VehicleData.length) {
          setSliderValue((prevSliderValue) => prevSliderValue + 1);
          setCurrentLocation(VehicleData[sliderValue]);
          setCurrentHeading(
            VehicleData[sliderValue]?.gps_rawdata?.split(",")[16]
          ); // Heading for the vehicle angle
        } else {
          setCurrentLocation(VehicleData[0]);
          setCurrentHeading(VehicleData[0]?.gps_rawdata?.split(",")[16]); // Heading for the vehicle angle
          setSliderValue(0);
          setPlay(false);
        }
      }, 1000 / speed);

      return () => clearInterval(interval);
    }
  }, [play, sliderValue, speed, VehicleData]);

  const navigate = useNavigate();

  const handleSpeedIncrease = () => {
    if (speed < 32) {
      setSpeed(speed * 2);
    }
  };

  const handleSpeedDecrease = () => {
    if (speed > 1) {
      setSpeed(speed / 2);
    }
  };

  const handleFetchData = () => {
    fetchDataInRange(from, to, param.vehicle_no);
  };

  const determineCenter = () => {
    if (VehicleData.length > 0) {
      return VehicleData[0];
    } else {
      return defaultCenter;
    }
  };

  return (
    <PageContainer
      title="Vehicle Profile"
      description="This is User Profile page"
    >
      <Grid container spacing={3}>
        <Grid item sm={12} md={12} lg={12} xs={12}>
          <BlankCard>
            <CardContent
              className="flex justify-between align-middle"
              style={{ backgroundColor: "#ECF2FF" }}
            >
              <div>
                <Typography variant="h4" style={{ fontFamily: "sans-serif" }}>
                  Route Replay
                </Typography>
                <Typography mt={1} style={{ fontSize: "14px" }}>
                  Vehicle &nbsp;&nbsp;&nbsp; ● &nbsp;&nbsp;&nbsp;
                  {param?.vehicle_no || "Vehicle Details"}
                </Typography>
              </div>
              <div>
                <Tooltip title="Back">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      navigate(-1);
                    }}
                  >
                    <IconArrowBack width={20} />
                  </Button>
                </Tooltip>
              </div>
            </CardContent>
          </BlankCard>
        </Grid>
        <Grid item sm={12} lg={12} xs={12}>
          <Grid container spacing={3}>
            <Grid
              item
              sm={12}
              ref={myElementRef}
              sx={{ position: "relative", padding: 0 }}
            >
              {!isLoaded ? (
                <h1>Loading...</h1>
              ) : (
                <>
                  <GoogleMap
                    options={mapOptions}
                    mapContainerStyle={mapContainerStyle}
                    center={determineCenter()}
                    zoom={14}
                  >
                    <MapControl
                      position="TOP_RIGHT"
                      style={{
                        width: "70%",
                        display: "flex",
                        justifyContent: "end",
                        alignItems: "center",
                        marginTop: "10px",
                        paddingLeft: "10px",
                        paddingRight: "10px",
                      }}
                    >
                      <div className="flex justify-center items-center mr-2 gap-3">
                        <DateTimeNew
                          value={from}
                          setValue={(newValue) => {
                            setFrom(newValue);
                          }}
                        />
                        <DateTimeNew
                          value={to}
                          setValue={(newValue) => setTo(newValue)}
                        />
                      </div>
                      {loading ? (
                        <Button
                          variant="contained"
                          color="primary"
                          style={{ marginRight: "10px", padding: "9px" }}
                          disabled
                        >
                          <CircularProgress size={24} />
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          style={{ marginRight: "10px", padding: "9px" }}
                          onClick={handleFetchData}
                        >
                          <SearchIcon />
                        </Button>
                      )}
                      <TempDrawer vehicles={vehicles} url="/vehiclemap" />
                    </MapControl>

                    <MapControl
                      position="BOTTOM_CENTER"
                      style={{ width: "100%" }}
                    >
                      <Box
                        sx={{
                          "& > :not(style)": {
                            m: 1,
                            bottom: "25px",
                            left: "40%",
                          },
                        }}
                      >
                        <Tooltip
                          title={`Speed: ${speed}x`}
                          placement="top"
                          PopperProps={{
                            modifiers: [
                              {
                                name: "zIndex",
                                enabled: true,
                                options: {
                                  zIndex: 150000,
                                },
                              },
                            ],
                          }}
                        >
                          <Fab
                            color="primary"
                            aria-label="decrease-speed"
                            onClick={handleSpeedDecrease}
                          >
                            <FastRewindIcon />
                          </Fab>
                        </Tooltip>
                        <Tooltip
                          title="Play/Pause"
                          placement="top"
                          PopperProps={{
                            modifiers: [
                              {
                                name: "zIndex",
                                enabled: true,
                                options: {
                                  zIndex: 150000,
                                },
                              },
                            ],
                          }}
                        >
                          <Fab
                            color="primary"
                            aria-label="play-pause"
                            onClick={() => setPlay(!play)}
                          >
                            {play ? <Pause /> : <PlayArrow />}
                          </Fab>
                        </Tooltip>
                        <Tooltip
                          title={`Speed: ${speed * 2}x`}
                          placement="top"
                          PopperProps={{
                            modifiers: [
                              {
                                name: "zIndex",
                                enabled: true,
                                options: {
                                  zIndex: 1500,
                                },
                              },
                            ],
                          }}
                        >
                          <Fab
                            color="primary"
                            aria-label="increase-speed"
                            onClick={handleSpeedIncrease}
                          >
                            <FastForwardIcon />
                          </Fab>
                        </Tooltip>
                      </Box>
                      <Box
                        sx={{
                          width: "90%",
                          position: "absolute",
                          bottom: "0",
                          left: "5%",
                        }}
                      >
                        <Slider
                          aria-label="Custom marks"
                          step={1}
                          min={1}
                          defaultValue={1}
                          max={VehicleData.length}
                          valueLabelFormat={(value) => `${value}`}
                          valueLabelDisplay="on"
                          onChange={handleSliderChange}
                          value={sliderValue}
                          sx={{
                            "& .MuiSlider-valueLabel": {
                              lineHeight: 1.2,
                              fontSize: 12,
                              background: "unset",
                              padding: 0,
                              width: 32,
                              height: 32,
                              borderRadius: "50% 50% 50% 0",
                              backgroundColor: "#5d87ff",
                              transformOrigin: "bottom left",
                              transform:
                                "translate(50%, -100%) rotate(-45deg) scale(0)",
                              "&::before": { display: "none" },
                              "&.MuiSlider-valueLabelOpen": {
                                transform:
                                  "translate(50%, -100%) rotate(-45deg) scale(1)",
                              },
                              "& > *": {
                                transform: "rotate(45deg)",
                              },
                            },
                          }}
                        />
                      </Box>
                    </MapControl>

                    {VehicleData.length > 0 && (
                      <Polyline
                        path={VehicleData}
                        options={{ strokeColor: "#FF5733" }}
                      />
                    )}

                    {VehicleData.length > 0 && (
                      <CustomMarker
                        position={currentLocation || VehicleData[0]}
                        heading={currentHeading || 0}
                        image={online}
                        status="inactive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveInfoWindow(!activeInfoWindow);
                        }}
                      />
                    )}
                    {currentLocation && (
                      <>
                        {activeInfoWindow && (
                          <CIWFRRNew
                            coordinates={currentLocation}
                            vehicleInfo={vehicleInfo}
                          />
                        )}
                      </>
                    )}
                  </GoogleMap>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

const MapControl = (props) => {
  const map = useGoogleMap();
  const ref = useRef();
  useEffect(() => {
    if (map && ref) {
      map.controls[window.google.maps.ControlPosition[props.position]].push(
        ref.current
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, ref]);
  return (
    <div style={props.style} ref={ref}>
      {props.children}
    </div>
  );
};

export default AllVehicleMap;
